import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Label } from "@arcadeai/design-system/components/ui/atoms/label";
import { RadioGroupItem } from "@arcadeai/design-system/components/ui/atoms/radio-group";
import { AlertTriangle } from "lucide-react";
import type React from "react";
import {
  getMethodBadge,
  getVerificationMethodDescription,
  getVerificationMethodTitle,
} from "../utils";

type VerificationRadioItemProps = {
  value: string;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  disabled?: boolean;
};

export function VerificationRadioItem({
  value,
  title,
  description,
  children,
  disabled = false,
}: VerificationRadioItemProps) {
  const inputId = `verification-${value}`;
  const badge = getMethodBadge(value);

  const displayTitle = title || getVerificationMethodTitle(value);
  const displayDescription =
    description || getVerificationMethodDescription(value);

  return (
    <Label
      className="group flex cursor-pointer items-start space-x-4 rounded-lg border-2 border-border bg-card p-5 transition-all duration-200 hover:bg-accent/50 has-[:checked]:border-primary has-[:checked]:bg-primary/8 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 dark:has-[:checked]:bg-primary/5"
      htmlFor={inputId}
    >
      <div className="mt-1">
        <RadioGroupItem
          aria-describedby={`${inputId}-description`}
          disabled={disabled}
          id={inputId}
          value={value}
        />
      </div>

      <div className="flex flex-1 items-start space-x-4">
        <div className="flex-1 space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold leading-none">{displayTitle}</div>
            {!!badge && (
              <Badge className="font-medium text-xs" variant={badge.variant}>
                {badge.text}
              </Badge>
            )}
          </div>
          <p
            className="text-muted-foreground text-sm leading-relaxed"
            id={`${inputId}-description`}
          >
            {displayDescription}
          </p>
          {children}
        </div>
      </div>
    </Label>
  );
}

export function NoVerifierWarning() {
  return (
    <div className="mt-4 flex items-start space-x-3 rounded-md border border-warning/20 bg-warning/10 p-4">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
      <div className="space-y-2">
        <p className="font-semibold text-sm text-warning">Security Warning</p>
        <p className="text-warning/80 text-xs leading-relaxed">
          This option is insecure and will be deprecated on August 1, 2025.
          Consider using a custom verifier for production applications.
        </p>
      </div>
    </div>
  );
}
